<template>
  <div :class="['m-hover-input', {'-readonly': !editable}]">
    <component
      :is="!autoWidth ? 'input' : 'span'"
      ref="content"
      :class="inputClasses"
      @mouseenter="!isDisabled ? editable = true : null"
      @mouseleave="!isFocused && !isDisabled ? editable = false : null"
      @keydown.enter.prevent="editable = false"
      @keydown="onKeydown"
      @focus="onFocus"
      @blur="onBlur"
      @paste="onPaste"
      @change.stop
      role="textbox"
      v-bind="bindAttributes"
      :value="value"
      v-html="autoWidth ? value : undefined"
      :key="inputKey"
      :aria-label="ariaLabel"
      :aria-disabled="isDisabled.toString()"
      :tabindex="0"
    />
  </div>
</template>

<script>
import * as contentEditableHelpers from '../../../helpers/contenteditable'

export default {
  name: 'FormHoverInput',
  components: {
  },
  props: {
    isDisabled: {
      type: Boolean,
      default: false
    },
    initValue: {
      type: [String, Number],
      required: true
    },
    autoWidth: {
      type: Boolean,
      default: false
    },
    maxLength: {
      type: Number,
      default: undefined
    },
    valueType: {
      type: String,
      default: 'Number',
      validator (value) {
        return ['String', 'Number'].includes(value)
      }
    },
    ariaLabel: {
      type: String,
      default: undefined
    }
  },
  data () {
    return {
      lastValue: null,
      value: null,
      editable: false,
      isFocused: false,
      inputKey: 0,
      inputClasses: ['form-control']
    }
  },
  watch: {
    editable: {
      handler () {
        const newValue = this.parseAccordingToType(this.getInputValue(this.$refs.content))
        if (this.validateValue(newValue)) {
          if (this.value !== newValue) {
            this.value = newValue
            this.$emit('change', newValue)
          }
        } else {
          this.updateValue(this.lastValue)
        }
      }
    }
  },
  created () {
    if (this.initValue !== undefined) {
      this.updateValue(this.initValue)
    }
  },
  computed: {
    bindAttributes () {
      if (!this.autoWidth) {
        return {
          readonly: !this.editable || this.isDisabled,
          ...(this.maxLength && { maxlength: this.maxLength })
        }
      } else {
        return {
          contenteditable: this.editable && !this.isDisabled,
          ...(this.maxLength && { style: { 'max-width': `${this.maxLength + 1}ch` } })
        }
      }
    }
  },
  methods: {
    validateValue (value) {
      switch (this.valueType) {
        case 'Number':
          return !(!value && value !== 0) && !isNaN(value)
        case 'String':
          return typeof value === 'string'
      }
    },
    validateKeyDownEvent (event) {
      const { key, ctrlKey, metaKey, target } = event
      return (!!key.match(/(arrow|backspace|meta|ctrl|tab)/i) ||
        (key.match(/^v/i) && (ctrlKey || metaKey)) ||
        (key.length === 1 && this.validateValue(key) &&
          this.validateExpectedInputLength(target, key)))
    },
    validateExpectedInputLength (input, newValue) {
      return !this.maxLength || (this.getInputValueLengthWithSelection(input) + newValue.length <= this.maxLength)
    },
    updateValue (val, forceRender = true) {
      this.value = this.lastValue = this.parseAccordingToType(val)
      this.editable = this.isFocused = false
      if (forceRender) this.forceRenderInput()
    },
    getValue () {
      return this.value
    },
    parseAccordingToType (value) {
      return this.valueType === 'Number' ? parseInt(value) : value.toString()
    },
    forceRenderInput () {
      this.inputKey += 1
    },
    targetIsInput (target) {
      return target.tagName === 'INPUT'
    },
    getInputValue (input) {
      return this.targetIsInput(input) ? input.value : input.innerHTML
    },
    getInputSelectionLength (input) {
      return this.targetIsInput(input)
        ? Math.max(0, input.selectionEnd - input.selectionStart)
        : window.getSelection().toString().length
    },
    getInputValueLengthWithSelection (input) {
      const length = this.getInputValue(input).length
      const selectionLength = this.getInputSelectionLength(input)
      return length - selectionLength
    },
    onFocus (event) {
      this.isFocused = true
      if (this.targetIsInput(event.target)) {
        event.target.select()
      } else {
        contentEditableHelpers.selectAllText(event.target)
      }
    },
    onBlur () {
      this.isFocused = this.editable = false
    },
    onKeydown (event) {
      this.editable = true
      if (!this.validateKeyDownEvent(event)) {
        event.preventDefault()
      }
    },
    onPaste (event) {
      const pastedText = event.clipboardData.getData('text')
      if (!this.validateValue(pastedText) || !this.validateExpectedInputLength(event.target, pastedText)) {
        event.preventDefault()
      }
    }
  }
}
</script>

<style lang="less">
@import (reference) "./../../../less/shared/utilities.less";
@import "@{frontend_static_less}/public/modules/m-hover-input.less";

.form-control {
  overflow-x: hidden;
}
</style>
